import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Select from 'react-select'

import { FilterDropdown } from '@/components/search/FilterDropdown'
import styles from '@/components/search/search.module.scss'
import { FILTER_RANGES } from '@/lib/constants'
import {
  selectFullActiveManufacturers,
  selectFullActiveRanges,
  selectVehicleRanges,
} from '@/store/selectors/search'
import { SearchFilterVehicleRange, SelectComponentProps } from '@/types/search'
import { Size } from '@/types/system'
import { isProd } from '@/lib/utilities/system'
import { filterClassName } from '@/lib/utilities/search'

const Icon = dynamic(() => import('@/components/styleguide/Icon'))

export const Models = ({
  title,
  toggleFilter,
  isFilterOpen = false,
  onChange,
}: SelectComponentProps) => {
  const selectedRanges = useSelector(selectFullActiveRanges)
  const availableRanges = useSelector(selectVehicleRanges)
  const selectedManufacturers = useSelector(selectFullActiveManufacturers)
  const [selectedOption, setSelectedOption] = useState<number[]>([])
  const [isFilterDisabled, setIsFilterDisabled] = useState(true)

  useEffect(() => {
    setSelectedOption(selectedRanges || [])
  }, [selectedRanges])

  useEffect(() => {
    if (selectedManufacturers.length === 0 && !isFilterOpen) {
      setIsFilterDisabled(true)
    } else {
      setIsFilterDisabled(false)
    }
  }, [selectedManufacturers, isFilterOpen])

  return (
    <FilterDropdown
      title={title}
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      isFilterDisabled={isFilterDisabled}
      hasSelectedOptions={selectedOption.length > 0}
      selectedOptions={
        selectedOption.length > 0
          ? selectedRanges.map((range: SearchFilterVehicleRange) => range.name)
          : ['All']
      }>
      {selectedManufacturers.length > 0 ? (
        <div className={styles['models-select-list']}>
          {selectedManufacturers
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(manufacturer => (
              <div
                key={manufacturer.id}
                className={styles['models-select']}>
                <span className={styles.label}>{manufacturer.name}</span>
                <Select
                  isMulti
                  onChange={(_, event) => {
                    if (event.action === 'remove-value' || event.action === 'pop-value') {
                      onChange({
                        value: event.removedValue.value,
                        type: 'multi',
                        filter: FILTER_RANGES,
                      })
                    } else if (event.action === 'select-option') {
                      onChange({
                        value: event.option.value,
                        type: 'multi',
                        filter: FILTER_RANGES,
                      })
                    }
                  }}
                  defaultValue={availableRanges
                    .filter(
                      (range: SearchFilterVehicleRange) =>
                        selectedRanges.includes(range.id) && range.manufacturer === manufacturer.id,
                    )
                    .map((range: SearchFilterVehicleRange) => ({
                      value: range.id,
                      label: range.name,
                    }))}
                  placeholder={
                    selectedRanges.filter(
                      (range: SearchFilterVehicleRange) => range.manufacturer !== manufacturer.id,
                    ).length > 0
                      ? 'None'
                      : 'Any'
                  }
                  closeMenuOnSelect={false}
                  isClearable={false}
                  classNames={{
                    container: () => styles['custom-select'],
                    control: ({ isFocused }) =>
                      `${styles.control} ${isFocused ? styles.focused : ''}`,
                    multiValue: () => styles.chip,
                    valueContainer: () => styles['value-container'],
                    option: ({ isFocused }) =>
                      `${styles.option} ${isFocused ? styles.focused : ''} ${!isProd() ? 'search__models-option search__models-option-' + filterClassName(manufacturer.name) : ''}`,
                    indicatorsContainer: () => styles['indicators-container'],
                    input: () => `${styles['select-input']}${!isProd() ? ' search__models-input' : ''}`,
                    placeholder: () => styles['select-placeholder'],
                  }}
                  components={{
                    IndicatorSeparator: () => <></>,
                    MultiValueLabel: ({ children }) => <span>{children}</span>,
                    MultiValueRemove: ({ innerProps }) => (
                      <button
                        {...innerProps}
                        className={styles.remove}>
                        <Icon
                          {...innerProps}
                          name="X"
                          size={Size.Medium}
                        />
                      </button>
                    ),
                    DropdownIndicator: ({ innerProps }) => (
                      <Icon
                        {...innerProps}
                        name="ChevronDown"
                        size={Size.Medium}
                      />
                    ),
                  }}
                  options={availableRanges
                    .filter(
                      (range: SearchFilterVehicleRange) => range.manufacturer === manufacturer.id,
                    )
                    .map((range: SearchFilterVehicleRange) => ({
                      value: range.id,
                      label: range.name,
                    }))}
                />
              </div>
            ))}
        </div>
      ) : (
        <p className={styles['error-text']}>
          {'There are no "Models" options available based on your current filter selections'}
        </p>
      )}
    </FilterDropdown>
  )
}
